/**
 * Norwegian bank account utilities.
 *
 * Norwegian retail bank accounts are 11 digits, written as
 * `XXXX XX XXXXX` in everyday use. The IBAN form prepends `NO` plus
 * a 2-digit check digit computed via the MOD-97 algorithm, producing
 * a 15-char IBAN like `NO12 3456 7890 1XX`.
 *
 * We collect the 11-digit form on the payout setup form (because
 * that's what Norwegians know off the top of their head) and convert
 * to IBAN for Stripe.
 */

// Strip spaces and any other non-digits.
const digitsOnly = value => (typeof value === 'string' ? value.replace(/\D/g, '') : '');

/**
 * Format a string of digits as `XXXX XX XXXXX` (the standard
 * Norwegian printed form) as the user types. Tolerates partial input.
 *
 * @param {string} value - raw input
 * @returns {string}
 */
export const formatNorwegianAccount = value => {
  const d = digitsOnly(value).slice(0, 11);
  if (d.length <= 4) {
    return d;
  }
  if (d.length <= 6) {
    return `${d.slice(0, 4)} ${d.slice(4)}`;
  }
  return `${d.slice(0, 4)} ${d.slice(4, 6)} ${d.slice(6)}`;
};

/**
 * MOD-11 weighted-sum checksum used by Norwegian bank account numbers.
 * Weights for the first 10 digits are 5,4,3,2,7,6,5,4,3,2 (right-to-left
 * 2..7 repeating). The 11th digit is the check digit. A valid account
 * number has (sum + check) divisible by 11.
 *
 * @param {string} value - account number (with or without spaces)
 * @returns {boolean}
 */
export const isValidNorwegianAccount = value => {
  const d = digitsOnly(value);
  if (d.length !== 11) {
    return false;
  }
  const weights = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 10; i += 1) {
    sum += parseInt(d[i], 10) * weights[i];
  }
  const remainder = sum % 11;
  // MOD-11 maps remainder 0 -> check digit 0; remainder 1 -> invalid
  // (no single check digit works); otherwise check digit = 11 - remainder.
  if (remainder === 1) {
    return false;
  }
  const expectedCheck = remainder === 0 ? 0 : 11 - remainder;
  return parseInt(d[10], 10) === expectedCheck;
};

/**
 * Convert a Norwegian 11-digit bank account number to IBAN format
 * using the MOD-97 IBAN check-digit algorithm.
 *
 *   IBAN := 'NO' + check(2) + bban(11)
 *   check := 98 - ((bban + countryCode + '00') as bigint mod 97)
 *   countryCode := letters mapped A=10, B=11, ..., so N=23, O=24
 *
 * Returns the IBAN with single-space grouping (`NO12 3456 7890 1XX`),
 * or null if the input isn't 11 digits. Does not verify the
 * Norwegian MOD-11 check — use isValidNorwegianAccount for that.
 *
 * @param {string} value - account number (with or without spaces)
 * @returns {string|null}
 */
export const norwegianAccountToIban = value => {
  const d = digitsOnly(value);
  if (d.length !== 11) {
    return null;
  }
  // N=23, O=24 per ISO 13616 letter-to-digit mapping (A=10...Z=35).
  const numeric = `${d}232400`;
  // BigInt MOD on the assembled string. JS number max is ~2^53, our
  // numeric string is 17 digits, comfortably inside Number range, but
  // BigInt is safer + clearer.
  const remainder = Number(BigInt(numeric) % 97n);
  const check = (98 - remainder).toString().padStart(2, '0');
  const raw = `NO${check}${d}`;
  // Group into 4-char blocks for readability: "NO12 3456 7890 1XX"
  return raw.replace(/(.{4})/g, '$1 ').trim();
};

/**
 * Compact IBAN (no spaces) suitable for Stripe's bank_account API.
 *
 * @param {string} value - account number (with or without spaces)
 * @returns {string|null}
 */
export const norwegianAccountToCompactIban = value => {
  const iban = norwegianAccountToIban(value);
  return iban ? iban.replace(/\s/g, '') : null;
};
